import React from "react";
import { FiCpu, FiTrendingDown, FiAlertOctagon, FiWind, FiBarChart2 } from "react-icons/fi";
import { AqiGauge, MiniBar, aqiMeta, pm25Color } from "./aqmHelpers";

const PRIORITY = {
  High:   { color: "#ef4444", bg: "#fee2e2" },
  Medium: { color: "#f59e0b", bg: "#fef3c7" },
  Low:    { color: "#3b82f6", bg: "#dbeafe" },
};

const AqmOutdoorAiPanel = ({ d }) => {
  const { summary, aiInsights, weather, stations } = d;
  const m = aqiMeta(summary.overallAqi);

  return (
    <div className="aqm-ai-panel">

      {/* Score cards */}
      <div className="aqm-ai-scores">
        <div className="aqm-ai-score-card">
          <AqiGauge aqi={summary.overallAqi} size={90} />
          <div>
            <div className="aqm-ai-sc-title">City Air Quality Score</div>
            <div className="aqm-ai-sc-sub">{m.label} — Based on {d.site.onlineStations} live monitoring stations across {d.site.location}</div>
          </div>
        </div>
        <div className="aqm-ai-score-card">
          <AqiGauge aqi={weather.forecast[2]?.aqi || 105} size={90} />
          <div>
            <div className="aqm-ai-sc-title">48h AQI Forecast</div>
            <div className="aqm-ai-sc-sub">Predicted AQI: <strong style={{ color: aqiMeta(weather.forecast[2]?.aqi || 105).color }}>{weather.forecast[2]?.aqi || 105}</strong> ({weather.forecast[2]?.condition}) — Rain Sunday will improve to ~75</div>
          </div>
        </div>
        <div className="aqm-ai-savings-card">
          <div className="aqm-ai-sav-val">{stations.filter(s => s.aqi > 150).length}</div>
          <div className="aqm-ai-sav-lbl">Stations — Unhealthy+</div>
          <div className="aqm-ai-sav-sub">AQI &gt; 150, affecting sensitive population</div>
          <div className="aqm-ai-sav-money">⚠ Public health advisory in effect</div>
        </div>
      </div>

      {/* AI Modules */}
      <div className="aqm-ai-modules">
        <div className="aqm-aim-card" style={{ borderTopColor: "#667eea" }}>
          <div className="aqm-aim-head"><FiBarChart2 size={16} color="#667eea" /> Station Comparison</div>
          <div className="aqm-aim-items">
            {stations.map(s => (
              <div key={s.id} className="aqm-aim-row">
                <span style={{ fontSize: 11 }}>{s.shortName}</span>
                <MiniBar value={s.aqi} max={300} color={aqiMeta(s.aqi).color} height={6} />
                <span style={{ color: aqiMeta(s.aqi).color, minWidth: 36, textAlign: "right" }}>{s.aqi}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#f97316" }}>
          <div className="aqm-aim-head"><FiWind size={16} color="#f97316" /> 5-Day AQI Forecast</div>
          <div className="aqm-aim-items">
            {weather.forecast.map((f, i) => {
              const fm = aqiMeta(f.aqi);
              return (
                <div key={i} className="aqm-aim-row">
                  <span style={{ fontSize: 11, minWidth: 55 }}>{f.day}</span>
                  <MiniBar value={f.aqi} max={250} color={fm.color} height={6} />
                  <span style={{ color: fm.color, minWidth: 36, textAlign: "right" }}>{f.aqi}</span>
                  <span style={{ fontSize: 10, color: "#9ca3af", minWidth: 50 }}>{f.windSpeed}km {f.windDir}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="aqm-aim-head"><FiAlertOctagon size={16} color="#ef4444" /> Anomaly Detection</div>
          <div className="aqm-aim-suggestions">
            <div className="aqm-aim-sug">🔴 Industrial Zone: PM2.5 spike 128 µg/m³ at 08:00 — 3× daily average. Likely unplanned emission event. Issue CPCB violation notice.</div>
            <div className="aqm-aim-sug">🟠 Highway NH-48: NO₂ 95 µg/m³ at 17:30 — corresponds with peak traffic. Predicted breach of 80 µg/m³ limit at 17:45.</div>
            <div className="aqm-aim-sug">🟡 Commercial Hub: PM10 steady rise from 12:00–16:00. Suspect road construction activity. Ground verification recommended.</div>
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#10b981" }}>
          <div className="aqm-aim-head"><FiTrendingDown size={16} color="#10b981" /> Pollution Reduction</div>
          <div className="aqm-aim-items">
            {[
              { label: "Traffic Control",     impact: 24, col: "#10b981" },
              { label: "Industrial Limits",   impact: 31, col: "#10b981" },
              { label: "Dust Suppression",    impact: 12, col: "#f59e0b" },
              { label: "Green Buffer Zone",   impact: 8,  col: "#3b82f6" },
            ].map(item => (
              <div key={item.label} className="aqm-aim-row">
                <span style={{ fontSize: 11 }}>{item.label}</span>
                <MiniBar value={item.impact} max={40} color={item.col} height={6} />
                <span style={{ color: item.col, minWidth: 50, textAlign: "right", fontSize: 11 }}>-{item.impact}% AQI</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insight Cards */}
      <div className="aqm-section-title" style={{ marginTop: 20 }}>Active AI Recommendations</div>
      <div className="aqm-insight-list">
        {aiInsights.map(ins => {
          const pm = PRIORITY[ins.priority];
          return (
            <div key={ins.id} className="aqm-insight-card" style={{ borderLeftColor: pm.color }}>
              <div className="aqm-ins-top">
                <span className="aqm-ins-icon">{ins.icon}</span>
                <div>
                  <div className="aqm-ins-title">{ins.title}</div>
                  <span className="aqm-badge" style={{ background: pm.bg, color: pm.color, fontSize: 11 }}>{ins.priority} Priority</span>
                </div>
              </div>
              <div className="aqm-ins-text">{ins.insight}</div>
              <div className="aqm-ins-footer">
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{ins.id}</span>
                <button className="aqm-ctrl-btn aqm-ctrl-purple" style={{ padding: "4px 12px", fontSize: 12 }}>{ins.action}</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="aqm-ai-footer-note">
        <FiCpu size={14} color="#667eea" />
        AI engine runs every 5 minutes — integrates live station data, wind forecast and historical patterns. Predictions have ±8% accuracy for 24h AQI window.
      </div>
    </div>
  );
};

export default AqmOutdoorAiPanel;
